import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

/**
 * Modern, modular Modal component following the Stride design system.
 */
const Modal = ({
  isOpen = true,
  onClose,
  hideModal, // alias for onClose (backward compatibility)
  title,
  subtitle,
  children,
  size = 'md', // 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
  loading = false,
  closeOnEsc = true,
  closeOnOutsideClick = true,
  zIndex = 999,
  className = '',
  widthClass = '',
  ariaLabel,
  headerAction,
}) => {
  const modalRef = useRef(null);
  const handleClose = onClose || hideModal;

  // Handle ESC key
  useEffect(() => {
    if (!isOpen || !closeOnEsc || !handleClose) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEsc, handleClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-5xl',
    full: 'max-w-[95vw]',
  };

  const appliedSize = widthClass || sizeClasses[size] || sizeClasses.md;

  const handleOverlayClick = (e) => {
    if (closeOnOutsideClick && e.target === e.currentTarget && handleClose && !loading) {
      handleClose(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel || title}
      onClick={handleOverlayClick}
      style={{ zIndex }}
      className="fixed inset-0 p-4 md:p-6 overflow-y-auto bg-slate-900/40 backdrop-blur-[2px] flex justify-center items-center animate-in fade-in duration-150"
    >
      <div
        ref={modalRef}
        className={`
          modal-container relative w-full ${appliedSize} my-auto bg-white rounded-2xl shadow-2xl border border-gray-100
          transform transition-all animate-in zoom-in-95 duration-150
          ${className}
        `}
      >
        {/* Render default header if title is passed as prop */}
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white rounded-t-2xl">
            <div className="min-w-0 pr-4">
              <h2 className="text-lg font-bold text-stride-heading tracking-tight truncate">
                {title}
              </h2>
              {subtitle && (
                <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
              )}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {headerAction}
              {handleClose && (
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => handleClose(false)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none disabled:opacity-40"
                  aria-label="Close modal"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>
        )}

        {children}
      </div>
    </div>,
    document.body
  );
};

/**
 * Modal Header compound component
 */
const ModalHeader = ({
  children,
  title,
  subtitle,
  onClose,
  className = '',
}) => {
  return (
    <div
      className={`flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white ${className}`}
    >
      <div className="min-w-0 pr-4">
        {title && (
          <h2 className="text-lg font-bold text-stride-heading tracking-tight truncate">
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
        )}
        {children}
      </div>

      {onClose && (
        <button
          type="button"
          onClick={() => onClose(false)}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none"
          aria-label="Close modal"
        >
          <X size={18} />
        </button>
      )}
    </div>
  );
};
ModalHeader.displayName = 'Modal.Header';

/**
 * Modal Body compound component
 */
const ModalBody = ({
  children,
  className = '',
  padding = 'p-6',
}) => {
  return (
    <div className={`${padding} ${className}`}>
      {children}
    </div>
  );
};
ModalBody.displayName = 'Modal.Body';

/**
 * Modal Footer compound component
 */
const ModalFooter = ({
  children,
  className = '',
}) => {
  return (
    <div
      className={`px-6 py-4 bg-gray-50/70 border-t border-gray-100 flex items-center justify-end gap-3 rounded-b-2xl ${className}`}
    >
      {children}
    </div>
  );
};
ModalFooter.displayName = 'Modal.Footer';

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;

